import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import {
	DIALOG_DATA,
	OPERATION_IDS,
} from '@atlassian/jira-issue-operations-commons/src/constants.tsx';
import getXsrfToken from '@atlassian/jira-platform-xsrf-token/src/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { asyncWrmRequire } from '@atlassian/jira-wrm/src/async-wrm.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type DialogProps = {
	styleClass: string;
	url: string;
	onSuccess: () => void;
	onFailure: () => void;
	issueId: number | null;
	accountId: string | null;
	elementToFocusOnClose?: string;
};

type CreateSubTaskIssueDialog = (arg1: { onDismissAfterCreate: () => void }) => Promise<void>;

type LoadDialog = (arg1: DialogProps) => Promise<void>;

const ACTION_SUBJECT_ID = 'issueOperationsMeatballMenu';

export const fireSuccessAnalytics = (analyticsEvent: UIAnalyticsEvent, styleClass: string) => {
	switch (styleClass) {
		case OPERATION_IDS.COMMENT_ISSUE:
			fireTrackAnalytics(analyticsEvent, 'comment created', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.LOG_WORK:
			fireTrackAnalytics(analyticsEvent, 'worklogModal added', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.CREATE_SUBTASK:
			fireTrackAnalytics(analyticsEvent, 'issue created', ACTION_SUBJECT_ID, {
				isSubtask: true,
			});
			break;
		case OPERATION_IDS.WATCH_ISSUE:
			fireTrackAnalytics(analyticsEvent, 'watchers startWatching', ACTION_SUBJECT_ID, {
				isSubtask: true,
			});
			break;
		case OPERATION_IDS.UNWATCH_ISSUE:
			fireTrackAnalytics(analyticsEvent, 'watchers stopWatching', ACTION_SUBJECT_ID, {
				isSubtask: true,
			});
			break;
		case OPERATION_IDS.LABELS:
			fireTrackAnalytics(analyticsEvent, 'issueFields updated', ACTION_SUBJECT_ID, {
				fieldType: 'labels',
				isReusableField: true,
				updatedField: 'labels',
			});
			break;
		case OPERATION_IDS.LINK_ISSUE:
			fireTrackAnalytics(analyticsEvent, 'linkedIssue succeeded', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.ADD_WEB_LINK:
			fireTrackAnalytics(analyticsEvent, 'webLink added', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.LINK_CONFLUENCE_PAGE:
			fireTrackAnalytics(analyticsEvent, 'linkedConfluencePage added', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.CLONE:
			fireTrackAnalytics(analyticsEvent, 'issue cloned', ACTION_SUBJECT_ID, {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				issueClonedIssueId: window.JIRA.IssueNavigator.getSelectedIssueId,
			});
			break;
		case OPERATION_IDS.DELETE:
			fireTrackAnalytics(analyticsEvent, 'issue deleted', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.RANK_TO_BOTTOM:
			fireTrackAnalytics(analyticsEvent, 'issue rankedToBottom', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.RANK_TO_TOP:
			fireTrackAnalytics(analyticsEvent, 'issue rankedToTop', ACTION_SUBJECT_ID);
			break;
		case OPERATION_IDS.ADD_VOTE:
			fireTrackAnalytics(analyticsEvent, 'field updated', ACTION_SUBJECT_ID, {
				fieldKey: 'votes',
				fieldType: 'votes',
			});
			break;
		default:
	}
};

export const loadDialog: LoadDialog = async ({
	styleClass,
	url,
	onSuccess,
	onFailure,
	issueId,
	accountId,
	elementToFocusOnClose,
}) => {
	// eslint-disable-next-line jira/wrm/no-load-bridge
	const FormDialog = await loadBridge({ name: 'jira/dialog/form-dialog' });

	const isVoteWatchOrAssign =
		styleClass === OPERATION_IDS.ADD_VOTE ||
		OPERATION_IDS.REMOVE_VOTE ||
		OPERATION_IDS.WATCH_ISSUE ||
		OPERATION_IDS.UNWATCH_ISSUE ||
		OPERATION_IDS.ASSIGN ||
		OPERATION_IDS.ASSIGN_TO_ME;

	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly "issueaction-assign-issue": { readonly id: "assign-dialog"; readonly issueMsg: "thanksIssueAssigned"; }; readonly "issueaction-assign-to-me hide-from-opsbar": { readonly id: "assign-dialog"; readonly issueMsg: "thanksIssueAssigned"; }; ... 5 more ...; readonly "issueaction-edit-labels": { ...; }; }'.
	const dialogData = DIALOG_DATA[styleClass];
	const ajaxOptions = {
		...(isVoteWatchOrAssign && { type: 'post' }),
		url,
		data: {
			decorator: 'dialog',
			inline: 'true',
			...(styleClass === OPERATION_IDS.ASSIGN && { id: issueId }),
			...(styleClass === OPERATION_IDS.ASSIGN_TO_ME && {
				id: issueId,
				atl_token: getXsrfToken(),
				assignee: accountId,
			}),
			...(styleClass === OPERATION_IDS.WATCH_ISSUE && {
				id: issueId,
				atl_token: getXsrfToken(),
				watch: 'watch',
			}),
			...(styleClass === OPERATION_IDS.UNWATCH_ISSUE && {
				id: issueId,
				atl_token: getXsrfToken(),
				watch: 'unwatch',
			}),
			...(styleClass === OPERATION_IDS.ADD_VOTE && {
				id: issueId,
				atl_token: getXsrfToken(),
				vote: 'vote',
			}),
			...(styleClass === OPERATION_IDS.REMOVE_VOTE && {
				id: issueId,
				atl_token: getXsrfToken(),
				vote: 'unvote',
			}),
		},
	};
	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	new FormDialog({
		...(dialogData && { id: dialogData?.id }),
		elementToFocusOnClose,
		ajaxOptions,
		autoClose: true,
		widthClass: 'large',
		onDialogFinished: onSuccess,
		onUnSuccessfulSubmit: onFailure,
		isIssueDialog: true,
	}).show();
};

export const openCreateSubTaskIssueDialog: CreateSubTaskIssueDialog = async ({
	onDismissAfterCreate,
}) => {
	await asyncWrmRequire(['wrc!com.atlassian.jira.jira-quick-edit-plugin:quick-create-issue']);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const form = window.JIRA.Forms.createSubtaskForm();
	form.bind('sessionComplete', () => {
		// Fires when at least one successful subtask creation
		onDismissAfterCreate();
	});
	form.asDialog().show();
};

export const permissionHelper = async (elementToFocusOnClose?: string) => {
	// eslint-disable-next-line jira/wrm/no-load-bridge
	const Dialog = await loadBridge({
		name: 'jira/admin-helper/dialog',
		wrmKeys: 'wrc!com.atlassian.jira.plugins.jira-admin-helper-plugin:dialog-resources',
	});
	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	new Dialog({
		id: 'permission-helper-dialog',
		widthClass: 'medium',
		...(fg('jfp-a11y-team_return-focus-to-more-btn') && { elementToFocusOnClose }),
		// @ts-expect-error - TS7006 - Parameter 'callback' implicitly has an 'any' type.
		content: function content(callback) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.require(
				['jira/admin-helper/permission-helper/content-loader'],
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(ContentLoader: any) => {
					ContentLoader.loadContent(this, callback);
				},
			);
		},
	}).show();
};

export const notificationHelper = async () => {
	// eslint-disable-next-line jira/wrm/no-load-bridge
	const Dialog = await loadBridge({
		name: 'jira/admin-helper/dialog',
		wrmKeys: 'wrc!com.atlassian.jira.plugins.jira-admin-helper-plugin:dialog-resources',
	});
	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	new Dialog({
		id: 'notification-helper-dialog',
		widthClass: 'medium',
		// @ts-expect-error - TS7006 - Parameter 'callback' implicitly has an 'any' type.
		content: function content(callback) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.require(
				['jira/admin-helper/notification-helper/content-loader'],
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(ContentLoader: any) => {
					ContentLoader.loadContent(this, callback);
				},
			);
		},
	}).show();
};
